import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '../../config';
import { getTodayYYYYMMDD } from '../../utils/dateUtils';
import './DailyCashbook.css';

const DailyCashbook = () => {
  const [dateFrom, setDateFrom] = useState(getTodayYYYYMMDD());
  const [dateTo, setDateTo] = useState(getTodayYYYYMMDD());

  const [cashbook, setCashbook] = useState(null);
  const [secondaryBalance, setSecondaryBalance] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const formatCurrency = (value) => {
    const amount = Number(value || 0);
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const fetchAll = useCallback(async () => {
    if (dateFrom && dateTo && new Date(dateFrom) > new Date(dateTo)) {
      setError('From date cannot be greater than To date.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const qs = `date_from=${encodeURIComponent(dateFrom)}&date_to=${encodeURIComponent(dateTo)}`;

      const dailyBalanceRequests = [
        fetch(`${API_BASE_URL}/api/secondary-balance-enquiry/daily-balances?date=${encodeURIComponent(dateTo)}`),
      ];
      if (dateFrom !== dateTo) {
        dailyBalanceRequests.push(
          fetch(`${API_BASE_URL}/api/secondary-balance-enquiry/daily-balances?date=${encodeURIComponent(dateFrom)}`)
        );
      }

      const [cashRes, secRes, closingDailyRes, openingDailyRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/partner/daily-cashbook?${qs}`),
        fetch(`${API_BASE_URL}/api/secondary-balance-enquiry?${qs}`),
        ...dailyBalanceRequests,
      ]);

      if (!cashRes.ok) {
        const payload = await cashRes.json().catch(() => ({}));
        throw new Error(payload.error || payload.message || 'Failed to fetch daily cashbook.');
      }
      if (!secRes.ok) {
        const payload = await secRes.json().catch(() => ({}));
        throw new Error(payload.error || payload.message || 'Failed to fetch secondary balance.');
      }

      const cashData = await cashRes.json();
      const secData = await secRes.json();

      if (closingDailyRes.ok) {
        const closingDaily = await closingDailyRes.json();
        let openingBalance = closingDaily.secondaryOpeningBalance;

        if (dateFrom !== dateTo && openingDailyRes?.ok) {
          const openingDaily = await openingDailyRes.json();
          openingBalance = openingDaily.secondaryOpeningBalance;
        }

        secData.secondaryOpeningBalance = openingBalance;
        secData.secondaryClosingBalance = closingDaily.secondaryClosingBalance;
        secData.secondaryDayNetBalance = dateFrom === dateTo
          ? closingDaily.secondaryDayNet
          : closingDaily.secondaryClosingBalance - openingBalance;
      }

      setCashbook(cashData);
      setSecondaryBalance(secData);
    } catch (err) {
      console.error('Daily cashbook fetch failed:', err);
      setError(err.message || 'Unexpected error occurred while loading daily cashbook.');
      setCashbook(null);
      setSecondaryBalance(null);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const debitRows = useMemo(() => cashbook?.debitEntries || [], [cashbook]);
  const creditRows = useMemo(() => cashbook?.creditEntries || [], [cashbook]);

  const totals = cashbook?.totals || { totalDebit: 0, totalCredit: 0, balance: 0 };

  const valueClass = (value) => {
    const v = Number(value || 0);
    if (v > 0) return 'positive';
    if (v < 0) return 'negative';
    return '';
  };

  return (
    <div className="daily-cashbook-page">
      <div className="daily-cashbook-card">
        <div className="daily-cashbook-header">
          <div>
            <h2>Daily Cashbook</h2>
            <p className="subtitle">Debit (Left) vs Credit (Right) for your selected date range</p>
          </div>
        </div>

        <div className="filters">
          <div className="filter-group">
            <label>From Date</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div className="filter-group">
            <label>To Date</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
          <div className="filter-actions">
            <button className="btn primary" onClick={fetchAll} disabled={loading}>
              {loading ? 'Loading…' : 'Apply'}
            </button>
            <button
              className="btn"
              onClick={() => {
                const today = getTodayYYYYMMDD();
                setDateFrom(today);
                setDateTo(today);
              }}
              disabled={loading}
            >
              Today
            </button>
          </div>
        </div>

        {error ? <div className="notice error">{error}</div> : null}

        <div className="two-col">
          <div className="col">
            <div className="col-title">Debit</div>
            <table className="cashbook-table">
              <thead>
                <tr>
                  <th>Particulars</th>
                  <th className="right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {debitRows.map((row) => (
                  <tr key={row.key}>
                    <td>{row.label}</td>
                    <td className={`right ${valueClass(row.amount)}`}>{formatCurrency(row.amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td className="total-label">Total Debit</td>
                  <td className="right total">{formatCurrency(totals.totalDebit)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="col">
            <div className="col-title">Credit</div>
            <table className="cashbook-table">
              <thead>
                <tr>
                  <th>Particulars</th>
                  <th className="right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {creditRows.map((row) => (
                  <tr key={row.key}>
                    <td>{row.label}</td>
                    <td className={`right ${valueClass(row.amount)}`}>{formatCurrency(row.amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td className="total-label">Total Credit</td>
                  <td className="right total">{formatCurrency(totals.totalCredit)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <div className="summary-row">
          <div className="summary-item">
            <div className="summary-label">Net Balance (Credit − Debit)</div>
            <div className={`summary-value ${valueClass(totals.balance)}`}>{formatCurrency(totals.balance)}</div>
          </div>
        </div>

        <div className="secondary-section">
          <div className="secondary-title">Secondary Balance (separate)</div>
          {!secondaryBalance ? (
            <div className="notice">No secondary balance data available.</div>
          ) : (
            <div className="secondary-grid">
              {secondaryBalance.secondaryOpeningBalance != null ? (
                <div className="secondary-card">
                  <div className="secondary-label">Opening Balance</div>
                  <div className={`secondary-value ${valueClass(secondaryBalance.secondaryOpeningBalance)}`}>
                    {formatCurrency(secondaryBalance.secondaryOpeningBalance)}
                  </div>
                </div>
              ) : null}
              <div className="secondary-card">
                <div className="secondary-label">Secondary Total Credit</div>
                <div className={`secondary-value ${valueClass(secondaryBalance.secondaryTotalCredit)}`}>
                  {formatCurrency(secondaryBalance.secondaryTotalCredit)}
                </div>
              </div>
              <div className="secondary-card">
                <div className="secondary-label">Secondary Total Debit</div>
                <div className={`secondary-value ${valueClass(secondaryBalance.secondaryTotalDebit)}`}>
                  {formatCurrency(secondaryBalance.secondaryTotalDebit)}
                </div>
              </div>
              <div className="secondary-card highlight">
                <div className="secondary-label">Period Net Balance</div>
                <div className={`secondary-value ${valueClass(secondaryBalance.secondaryOverallBalance)}`}>
                  {formatCurrency(secondaryBalance.secondaryOverallBalance)}
                </div>
              </div>
              {secondaryBalance.secondaryClosingBalance != null ? (
                <div className="secondary-card highlight">
                  <div className="secondary-label">Closing Balance</div>
                  <div className={`secondary-value ${valueClass(secondaryBalance.secondaryClosingBalance)}`}>
                    {formatCurrency(secondaryBalance.secondaryClosingBalance)}
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </div>

        {cashbook?.range ? (
          <div className="range-note">
            Showing daily cashbook between <strong>{cashbook.range.date_from}</strong> and <strong>{cashbook.range.date_to}</strong>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default DailyCashbook;


